/**
 * Shared types for the eSIM installation flow.
 *
 * The flow has three axes:
 *   - Device: which OS / phone family the user is on (drives Settings paths
 *     and step content).
 *   - Method: how the user wants to feed the activation profile into their
 *     phone (scan QR, type manually, or — on iPhone iOS 17.4+ — tap a deep
 *     link).
 *   - eSIM state: what the backend thinks of this eSIM right now. We use it
 *     to switch the page between "guide the user through install", "guide
 *     them through Connect", and "show terminal states like expired".
 *
 * These are kept here so data/steps.ts, hooks/*, analytics.ts, and the
 * page components can all speak the same vocabulary without re-declaring.
 */

/** Top-level OS choice the user is shown as a tab. */
export type DeviceOs = "ios" | "android"

/**
 * Concrete device variant. Android splits further by manufacturer because
 * Settings paths diverge (Samsung's "SIM card manager" ≠ Pixel's "Network
 * & internet" ≠ Xiaomi's "Manage eSIM"). `android-generic` is the catch-all
 * for OPPO / Vivo / Huawei / OnePlus / etc.
 */
export type DeviceKey =
  | "ios"
  | "android-samsung"
  | "android-pixel"
  | "android-xiaomi"
  | "android-generic"

/** Method the user picks within a device to feed the eSIM profile. */
export type InstallMethod =
  | "qr" // Scan the QR code from another screen / printout.
  | "manual" // Type SM-DP+ + activation code by hand.
  | "deeplink" // iOS 17.4+ only: tap the installation link to hand off to the system.

/** Android manufacturer chip — only meaningful when device.os === "android". */
export type AndroidBrand =
  | "samsung"
  | "pixel"
  | "xiaomi"
  | "generic"

/**
 * Business state of a single eSIM. Drives which sections of the page are
 * collapsed / surfaced. The mapping from raw backend status strings to this
 * union is owned by useEsimState — never read raw status fields in UI code.
 */
export type EsimStatus =
  | "pending" // Provisioned but never installed on any device.
  | "installing" // User has started install (e.g. tapped deeplink) but not confirmed.
  | "installed" // Profile is on a device but data hasn't started flowing.
  | "active" // Connected, data plan is running.
  | "expired" // Validity window elapsed.
  | "paused" // Suspended by the user or by the carrier.
  | "unknown" // We couldn't classify — render as pending to be safe.

/** Stage the user is in within a given device flow. */
export type InstallStage = "install" | "connect"

/** Resolves an OS + brand into the concrete DeviceKey used by data/steps.ts. */
export function deviceKeyFor(
  os: DeviceOs,
  brand?: AndroidBrand
): DeviceKey {
  if (os === "ios") return "ios"
  switch (brand) {
    case "samsung":
      return "android-samsung"
    case "pixel":
      return "android-pixel"
    case "xiaomi":
      return "android-xiaomi"
    default:
      return "android-generic"
  }
}

/** Splits a DeviceKey back into its OS + brand parts. */
export function splitDeviceKey(key: DeviceKey): {
  os: DeviceOs
  brand: AndroidBrand | null
} {
  if (key === "ios") return { os: "ios", brand: null }
  const brand = key.replace(/^android-/, "") as AndroidBrand
  return { os: "android", brand }
}
